// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import { DataTableHeadProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHeader";
import { DataTableRowProps } from "@eisinc/MobiusOverrides/DataTable/DataTableRow";
import { GridContainerProps, GridOffset } from "@eisinc/MobiusOverrides/GridContainer/GridContainer";
import {
    ShipmentPackageDataStyles,
    ShipmentTrackingUrlsStyles,
} from "@eisinc/Overrides/Widgets/OrderDetails/OrderDetailsShipmentPackages";
import { screen } from "@eisinc/XDL/Helpers";
import { printStyles } from "@eisinc/XDL/Utilities";
import { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface ShipmentPackagesSharedStyles {
    tableHeadStyle?: DataTableHeadProps;
    tableHeader?: DataTableHeaderProps;
    tableRowStyle?: DataTableRowProps;
    tableCellGridContainerStyle?: GridContainerProps;
    trackingNumberCellWrapper?: InjectableCss;
    urlTrackingStyle?: LinkPresentationProps;
    shipmentPackageDataStyles?: ShipmentPackageDataStyles;
    shipmentTrackingUrlsStyles?: ShipmentTrackingUrlsStyles;
}

export const shipmentPackagesSharedStyles: ShipmentPackagesSharedStyles = {
    tableHeadStyle: {
        css: css`
            ${OrderDetailsTableStyle.tableHeadStyle.css}
            ${screen("lte", "md")`
                display: none;
            `}
            @media print {
                display: table-row;
                ${screen("lte", "md")`
                     ${TypographyStyle} {
                         display: block;
                     }
                 `}
            }
        `,
    },
    tableHeader: {
        typographyProps: {
            size: 12,
            weight: 500,
        },
        css: css`
            width: 15%;
            height: 30px;
            border-top: 0;

            @media print {
                &.hide-print {
                    display: none;
                }
            }
        `,
    },
    tableRowStyle: {
        evenRowCss: css`
            background: transparent;
        `,
        css: css`
            border-bottom: 1px solid ${getColor("common.ui4")};

            td {
                height: 65px;
                vertical-align: middle;

                ${screen("lte", "md")`
                         &:first-child {
                             margin-top: 16px;
                         }
                         &:last-child {
                             margin-block: 16px;
                         }
                     `}
                @media print {
                    display: table-cell;
                }
            }
            &&:hover,
            &&:focus {
                background: transparent;
                cursor: unset;
            }
            .hide-print {
                ${printStyles.hideOnPrint}
            }
            &&.accordion-color {
                border-bottom: 0;
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    tableCellGridContainerStyle: {
        css: css`
            ${GridOffset} {
                height: 100%;
            }
        `,
    },
    urlTrackingStyle: {
        typographyProps: {
            css: css`
                font-size: 13px !important;
            `,
        },
    },
    shipmentPackageDataStyles: {
        docLink: {
            css: css`
                &,
                &:focus {
                    outline: 0;
                }
            `,
            typographyProps: {
                css: css`
                    font-size: 16px !important;
                `,
            },
        },
    },
    trackingNumberCellWrapper: {
        css: css`
            max-width: 26ch;
            word-break: break-all;
            white-space: break-spaces;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    shipmentTrackingUrlsStyles: {
        orderDetailsShipmentPackagesRowStyles: {
            shipmentModal: {
                cssOverrides: {
                    modalTitle: css`
                        padding: 10px 20px;
                    `,
                    modalBody: css`
                        ${({ theme }: any) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        position: relative;
                                        text-align: left;
                                        height: auto;
                                        margin: 0;
                                        max-width: 95%;
                                        max-height: 92vh;
                                    `,
                                    null,
                                    css`
                                        min-width: 450px;
                                        height: auto;
                                        top: 0px;
                                        box-shadow: ${theme?.shadows[3]};
                                        max-width: 80%;
                                        max-height: 92vh;
                                    `,
                                    null,
                                    null,
                                ],
                                "min",
                            )};
                    `,
                },
            },
            resultDataCell: {
                css: css`
                    ${screen("lt", "sm")`
                span {
                    font-size: 13px;
                    padding-top: 3px;    
                }
            `}
                `,
            },
            modalData: {
                css: css`
                    font-weight: bold;
                `,
            },
            modalDataWrapper: {
                css: css`
                    padding-bottom: 10px;
                    ${screen("lt", "sm")`
                        border-bottom: 1px solid black;
                    `}
                `,
            },
        },
    },
};
